// NoCleanBuyFallback — when the live engine has no clean Strong/Playable BUY
// to surface, this widget turns the empty state into a value statement instead
// of a void. Counts come live from market_feed (Polymarket-only). Self-hides
// when at least one Strong or Playable BUY exists.
//
// Honest by design: every count is a real row count from the current scan.
// No projections, no padding. If everything reads zero, the widget renders
// a single restrained line and links to /feed.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface Counts {
  cleanBuys: number;       // tier in (strong, playable) AND is_actionable
  nearTrigger: number;     // tier = track AND is_actionable false but route ok
  traps: number;           // edge_after_costs <= -0.02 AND route ok
  resolvingToday: number;  // end_date inside next 24h
}

const EMPTY: Counts = { cleanBuys: 0, nearTrigger: 0, traps: 0, resolvingToday: 0 };

export default function NoCleanBuyFallback() {
  const [counts, setCounts] = useState<Counts | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const now = new Date().toISOString();
        const in24h = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();

        const [buys, near, traps, resolving] = await Promise.all([
          supabase
            .from("market_feed")
            .select("id", { count: "exact", head: true })
            .eq("platform", "polymarket")
            .eq("is_actionable", true)
            .in("tier", ["strong", "playable"])
            .eq("route_status", "ok"),
          supabase
            .from("market_feed")
            .select("id", { count: "exact", head: true })
            .eq("platform", "polymarket")
            .eq("tier", "track")
            .eq("route_status", "ok"),
          supabase
            .from("market_feed")
            .select("id", { count: "exact", head: true })
            .eq("platform", "polymarket")
            .eq("route_status", "ok")
            .lte("edge_after_costs", -0.02),
          supabase
            .from("market_feed")
            .select("id", { count: "exact", head: true })
            .eq("platform", "polymarket")
            .eq("route_status", "ok")
            .gte("end_date", now)
            .lte("end_date", in24h),
        ]);
        if (cancelled) return;
        setCounts({
          cleanBuys: buys.count ?? 0,
          nearTrigger: near.count ?? 0,
          traps: traps.count ?? 0,
          resolvingToday: resolving.count ?? 0,
        });
      } catch {
        if (!cancelled) setCounts(EMPTY);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Self-hide while loading or when at least one clean BUY exists.
  if (loading || !counts) return null;
  if (counts.cleanBuys > 0) return null;

  const hasAnything = counts.nearTrigger + counts.traps + counts.resolvingToday > 0;

  return (
    <Link
      to="/feed"
      className="block max-w-2xl mx-auto mb-4 rounded-xl border border-border/60 bg-card hover:border-primary/35 transition-colors px-4 py-3.5 group"
    >
      <p className="text-[10px] font-body uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1.5">
        <Activity className="w-3 h-3 text-primary" /> Right now on Polymarket
      </p>

      <p className="mt-1.5 text-[15px] sm:text-base font-display font-semibold text-foreground leading-snug">
        No clean BUY right now. That is the edge.
      </p>

      {hasAnything ? (
        <p className="mt-1 text-[12px] font-body text-muted-foreground leading-snug">
          Decyzon found{" "}
          <span className="text-foreground tabular-nums">{counts.nearTrigger}</span> markets near trigger,{" "}
          <span className="text-foreground tabular-nums">{counts.traps}</span> prices worth avoiding, and{" "}
          <span className="text-foreground tabular-nums">{counts.resolvingToday}</span> resolving today.
        </p>
      ) : (
        <p className="mt-1 text-[12px] font-body text-muted-foreground leading-snug">
          The book is quiet. Better to wait than to chase.
        </p>
      )}

      <p className="mt-2 inline-flex items-center gap-1 text-[10.5px] font-body uppercase tracking-wider text-primary group-hover:underline">
        Open the feed <ArrowRight className="w-3 h-3" />
      </p>
    </Link>
  );
}
